/**
 * 🛟 V2X设备管理器 - 智能设备3D交互系统
 *
 * 这个管理器就像"智能设备的指挥官"👨‍💼，负责：
 * 1. 📍 在3D场景中添加各种设备（RSU、摄像头、雷达等）
 * 2. 🖱️ 为每个设备绑定点击事件，无需手动raycasting
 * 3. 🎨 自动关联气泡系统，点击即显示设备信息
 * 4. 🔄 统一管理设备状态变化和视觉效果
 * 5. 🎯 提供高性能的3D交互体验
 */

import * as THREE from 'three';
import { BubbleManager, BubbleConfig } from './BubbleManager';
import { CoordinateSystem } from './CoordinateSystem';
import type { MoveAnimationConfig } from './VehicleManager'; // 复用VehicleManager的动画配置

// 动态导入GSAP（修复版本）
let gsap: any = null;
(async () => {
  try {
    const gsapModule = await import('gsap');
    gsap = gsapModule.default || gsapModule; // 处理ES模块导入
    console.log('[DeviceManager] GSAP加载成功，将使用高级动画。');
  } catch (error) {
    console.warn('[DeviceManager] GSAP未找到，将使用基础动画。');
  }
})();

/**
 * 设备类型定义
 */
export type DeviceType =
  | 'rsu'
  | 'camera'
  | 'radar'
  | 'vehicle'
  | 'sensor'
  | 'gateway';

/**
 * 设备状态定义
 */
export type DeviceStatus =
  | 'online'
  | 'offline'
  | 'warning'
  | 'maintenance'
  | 'error'
  | 'moving';

/**
 * 设备配置接口
 */
export interface DeviceConfig {
  /** 设备唯一标识 */
  id: string;
  /** 设备名称 */
  name: string;
  /** 设备类型 */
  type: DeviceType;
  /** 设备状态 */
  status: DeviceStatus;
  /** 3D世界坐标位置 */
  position?: THREE.Vector3;
  /** 地理坐标位置（纬度、经度、高度） */
  geographicPosition?: {
    latitude: number;
    longitude: number;
    altitude?: number;
  };
  /** 设备数据 */
  data?: Record<string, any>;
  /** 自定义3D模型URL（可选，不提供则使用默认几何体） */
  modelUrl?: string;
  /** 设备大小缩放 */
  scale?: number;
  /** 点击回调函数 */
  onClick?: (device: DeviceInfo) => void;
  /** 气泡配置（可选，不提供则使用默认配置） */
  bubbleConfig?: Partial<BubbleConfig>;
}

/**
 * 设备信息接口（包含运行时状态）
 */
export interface DeviceInfo extends DeviceConfig {
  /** 3D对象引用 */
  object3D: THREE.Object3D;
  /** 是否显示气泡 */
  bubbleVisible: boolean;
  /** 创建时间 */
  createdAt: Date;
  /** 最后更新时间 */
  updatedAt: Date;
}

/**
 * 设备样式配置
 */
interface DeviceVisualConfig {
  color: number;
  emissive: number;
  emissiveIntensity: number;
  hoverColor: number;
  size: number;
}

/**
 * 🛟 V2X设备管理器
 */
export class DeviceManager {
  private scene: THREE.Scene;
  private camera: THREE.Camera;
  private container: HTMLElement;
  private bubbleManager?: BubbleManager;
  private coordinateSystem?: CoordinateSystem;

  private devices: Map<string, DeviceInfo> = new Map();
  private deviceGroup: THREE.Group;
  private raycaster = new THREE.Raycaster();
  private mouse = new THREE.Vector2();
  private hoveredDevice: DeviceInfo | null = null;
  private boundClick = this.handleClick.bind(this);
  private boundMouseMove = this.handleMouseMove.bind(this);

  private readonly DEVICE_STYLES: Record<DeviceType, DeviceVisualConfig> = {
    rsu: {
      color: 0x00ff00,
      emissive: 0x004400,
      emissiveIntensity: 0.3,
      hoverColor: 0x44ff44,
      size: 3,
    },
    camera: {
      color: 0x0088ff,
      emissive: 0x002244,
      emissiveIntensity: 0.3,
      hoverColor: 0x44aaff,
      size: 2.5,
    },
    radar: {
      color: 0xff8800,
      emissive: 0x442200,
      emissiveIntensity: 0.3,
      hoverColor: 0xffaa44,
      size: 2.8,
    },
    vehicle: {
      color: 0xff0066,
      emissive: 0x440022,
      emissiveIntensity: 0.3,
      hoverColor: 0xff4488,
      size: 4,
    },
    sensor: {
      color: 0x8800ff,
      emissive: 0x220044,
      emissiveIntensity: 0.3,
      hoverColor: 0xaa44ff,
      size: 2,
    },
    gateway: {
      color: 0xffff00,
      emissive: 0x444400,
      emissiveIntensity: 0.3,
      hoverColor: 0xffff44,
      size: 3.5,
    },
  };

  constructor(
    scene: THREE.Scene,
    camera: THREE.Camera,
    container: HTMLElement,
    bubbleManager?: BubbleManager,
    coordinateSystem?: CoordinateSystem
  ) {
    this.scene = scene;
    this.camera = camera;
    this.container = container;
    this.bubbleManager = bubbleManager;
    this.coordinateSystem = coordinateSystem;
    this.deviceGroup = new THREE.Group();
    this.deviceGroup.name = 'V2X-Devices';
    this.scene.add(this.deviceGroup);
    this.initializeInteractionSystem();
  }

  private initializeInteractionSystem(): void {
    this.container.addEventListener('click', this.boundClick);
    this.container.addEventListener('mousemove', this.boundMouseMove);
  }

  async addDevice(config: DeviceConfig): Promise<DeviceInfo> {
    if (this.devices.has(config.id)) {
      this.removeDevice(config.id);
    }

    let position: THREE.Vector3;
    if (config.geographicPosition && this.coordinateSystem) {
      position = this.coordinateSystem.createVector3FromGeographic(
        config.geographicPosition
      );
    } else {
      position = config.position?.clone() ?? new THREE.Vector3(0, 0, 0);
    }

    const object3D = await this.createDevice3D(config, position);
    const deviceInfo: DeviceInfo = {
      ...config,
      object3D,
      bubbleVisible: false,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    this.devices.set(config.id, deviceInfo);
    this.deviceGroup.add(object3D);

    if (this.bubbleManager) {
      this.createDeviceBubble(deviceInfo);
    }
    return deviceInfo;
  }

  private async createDevice3D(
    config: DeviceConfig,
    position: THREE.Vector3
  ): Promise<THREE.Object3D> {
    const deviceStyle = this.DEVICE_STYLES[config.type];
    const scale = config.scale || 1;

    if (config.modelUrl) {
      try {
        const model = await this.loadDeviceModel(config.modelUrl);
        model.position.copy(position);
        model.scale.setScalar(scale);
        model.userData.deviceId = config.id;
        model.name = `device-${config.id}`;
        return model;
      } catch (error) {
        console.warn(
          `Failed to load custom model for ${config.id}, using default geometry.`
        );
      }
    }

    const geometry = new THREE.SphereGeometry(deviceStyle.size * scale, 16, 16);
    const material = new THREE.MeshStandardMaterial({
      color: deviceStyle.color,
      emissive: deviceStyle.emissive,
      emissiveIntensity: deviceStyle.emissiveIntensity,
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.copy(position);
    mesh.position.y += deviceStyle.size * scale;
    mesh.userData.deviceId = config.id;
    mesh.userData.originalColor = deviceStyle.color;
    mesh.userData.hoverColor = deviceStyle.hoverColor;
    mesh.name = `device-${config.id}`;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    return mesh;
  }

  private async loadDeviceModel(url: string): Promise<THREE.Object3D> {
    const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');
    const loader = new GLTFLoader();
    const gltf = await loader.loadAsync(url);
    gltf.scene.traverse(child => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
    return gltf.scene;
  }

  private createDeviceBubble(device: DeviceInfo): void {
    if (!this.bubbleManager) return;
    const bubbleConfig: BubbleConfig = {
      id: `device-bubble-${device.id}`,
      target: device.object3D,
      title: device.name,
      content: {
        status: device.status,
        data: { ID: device.id, ...device.data },
      },
      ...device.bubbleConfig,
    };
    this.bubbleManager.addBubble(bubbleConfig);
  }

  private getDeviceTypeDisplayName = (type: DeviceType): string =>
    ({
      rsu: 'RSU',
      camera: 'Camera',
      radar: 'Radar',
      vehicle: 'Vehicle',
      sensor: 'Sensor',
      gateway: 'Gateway',
    })[type] || type;

  private getStatusDisplayName = (status: DeviceStatus): string =>
    ({
      online: 'Online',
      offline: 'Offline',
      warning: 'Warning',
      maintenance: 'Maintenance',
      error: 'Error',
      moving: 'Moving',
    })[status] || status;

  private handleClick(event: MouseEvent): void {
    const intersects = this.getIntersects(event);
    if (intersects.length > 0) {
      const deviceId = this.findDeviceId(intersects[0].object);
      if (deviceId) {
        const device = this.devices.get(deviceId);
        if (device) this.handleDeviceClick(device);
      }
    }
  }

  private handleMouseMove(event: MouseEvent): void {
    const intersects = this.getIntersects(event);
    if (this.hoveredDevice) {
      this.updateDeviceHoverState(this.hoveredDevice, false);
      this.hoveredDevice = null;
      this.container.style.cursor = 'default';
    }
    if (intersects.length > 0) {
      const deviceId = this.findDeviceId(intersects[0].object);
      if (deviceId) {
        const device = this.devices.get(deviceId);
        if (device) {
          this.hoveredDevice = device;
          this.updateDeviceHoverState(device, true);
          this.container.style.cursor = 'pointer';
        }
      }
    }
  }

  private getIntersects(event: MouseEvent): THREE.Intersection[] {
    const rect = this.container.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, this.camera);
    return this.raycaster.intersectObjects(this.deviceGroup.children, true);
  }

  private findDeviceId = (object: THREE.Object3D): string | null =>
    object.userData.deviceId ?? object.parent?.userData.deviceId ?? null;

  private updateDeviceHoverState(device: DeviceInfo, isHovered: boolean): void {
    device.object3D.traverse(child => {
      if (
        child instanceof THREE.Mesh &&
        child.material instanceof THREE.MeshStandardMaterial
      ) {
        if (isHovered) {
          child.material.color.setHex(
            device.object3D.userData.hoverColor ||
              device.object3D.userData.originalColor
          );
          child.material.emissiveIntensity = 0.5;
        } else {
          child.material.color.setHex(device.object3D.userData.originalColor);
          child.material.emissiveIntensity = 0.3;
        }
      }
    });
  }

  private handleDeviceClick(device: DeviceInfo): void {
    device.onClick?.(device);
    if (this.bubbleManager) {
      this.devices.forEach(d => {
        if (d.id !== device.id) this.hideDeviceBubble(d.id);
      });
      this.toggleDeviceBubble(device.id);
    }
  }

  toggleDeviceBubble = (deviceId: string) => {
    const device = this.devices.get(deviceId);
    if (device)
      device.bubbleVisible
        ? this.hideDeviceBubble(deviceId)
        : this.showDeviceBubble(deviceId);
  };

  showDeviceBubble(deviceId: string): boolean {
    const device = this.devices.get(deviceId);
    if (!device || !this.bubbleManager) return false;
    const success = this.bubbleManager.showBubble(`device-bubble-${deviceId}`);
    if (success) device.bubbleVisible = true;
    return success;
  }

  hideDeviceBubble(deviceId: string): boolean {
    const device = this.devices.get(deviceId);
    if (!device || !this.bubbleManager) return false;
    const success = this.bubbleManager.hideBubble(`device-bubble-${deviceId}`);
    if (success) device.bubbleVisible = false;
    return success;
  }

  removeDevice(deviceId: string): boolean {
    const device = this.devices.get(deviceId);
    if (!device) return false;
    this.bubbleManager?.removeBubble(`device-bubble-${deviceId}`);
    this.deviceGroup.remove(device.object3D);
    // Proper disposal of resources
    device.object3D.traverse(child => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        Array.isArray(child.material)
          ? child.material.forEach(m => m.dispose())
          : child.material.dispose();
      }
    });
    this.devices.delete(deviceId);
    return true;
  }

  updateDeviceStatus(deviceId: string, status: DeviceStatus): boolean {
    const device = this.devices.get(deviceId);
    if (!device) return false;
    device.status = status;
    device.updatedAt = new Date();
    this.updateDeviceBubbleContent(device);
    console.log(
      `[DeviceManager] Device status updated: ${deviceId} -> ${status}`
    );
    return true;
  }

  getDevice = (deviceId: string) => this.devices.get(deviceId);
  getAllDevices = () => Array.from(this.devices.values());
  getDevicesByType = (type: DeviceType) =>
    this.getAllDevices().filter(d => d.type === type);
  getDevicesByStatus = (status: DeviceStatus) =>
    this.getAllDevices().filter(d => d.status === status);
  showAllBubbles = () => this.devices.forEach(d => this.showDeviceBubble(d.id));
  hideAllBubbles = () => this.devices.forEach(d => this.hideDeviceBubble(d.id));

  dispose(): void {
    this.container.removeEventListener('click', this.boundClick);
    this.container.removeEventListener('mousemove', this.boundMouseMove);
    this.getAllDevices().forEach(d => this.removeDevice(d.id));
    this.scene.remove(this.deviceGroup);
  }

  moveDeviceToLocation(deviceId: string, config: MoveAnimationConfig): boolean {
    const device = this.devices.get(deviceId);
    if (!device || !this.coordinateSystem) return false;

    const targetPosition = this.coordinateSystem.createVector3FromGeographic(
      config.targetGeographicPosition
    );
    const originalStatus = device.status;
    device.status = 'moving';

    if (gsap) {
      gsap.to(device.object3D.position, {
        ...targetPosition,
        duration: config.duration || 2,
        ease: config.ease || 'power2.inOut',
        onUpdate: () => {
          this.updateDeviceBubbleContent(device);
          config.onUpdate?.(
            device.object3D.position.distanceTo(targetPosition) /
              device.object3D.position.distanceTo(targetPosition)
          );
        },
        onComplete: () => {
          device.status = originalStatus;
          this.updateDeviceBubbleContent(device);
          config.onComplete?.();
        },
      });
    } else {
      this.moveDeviceBasic(
        device,
        targetPosition,
        (config.duration || 2) * 1000,
        config,
        originalStatus
      );
    }
    return true;
  }

  private moveDeviceBasic(
    device: DeviceInfo,
    target: THREE.Vector3,
    duration: number,
    config: MoveAnimationConfig,
    originalStatus: DeviceStatus
  ) {
    const startPos = device.object3D.position.clone();
    const startTime = Date.now();
    const animate = () => {
      const progress = Math.min((Date.now() - startTime) / duration, 1);
      device.object3D.position.lerpVectors(startPos, target, progress);
      this.updateDeviceBubbleContent(device);
      config.onUpdate?.(progress);
      if (progress < 1) requestAnimationFrame(animate);
      else {
        device.status = originalStatus;
        this.updateDeviceBubbleContent(device);
        config.onComplete?.();
      }
    };
    animate();
  }

  private updateDeviceBubbleContent(device: DeviceInfo): void {
    if (!this.bubbleManager) return;
    const bubbleId = `device-bubble-${device.id}`;

    // updateBubbleContent 只能更新 BubbleConfig 的 content 字段
    this.bubbleManager.updateBubbleContent(bubbleId, {
      status: device.status,
      data: {
        ID: device.id,
        Type: this.getDeviceTypeDisplayName(device.type),
        Status: this.getStatusDisplayName(device.status),
        ...device.data,
      },
    });
  }
}

export function createDeviceManager(
  scene: THREE.Scene,
  camera: THREE.Camera,
  container: HTMLElement,
  bubbleManager?: BubbleManager,
  coordinateSystem?: CoordinateSystem
): DeviceManager {
  return new DeviceManager(
    scene,
    camera,
    container,
    bubbleManager,
    coordinateSystem
  );
}

export type { MoveAnimationConfig };
